import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import usuarioService from '../services/usuarioService';
import api from '../services/api';
import { tienePermiso, CODIGOS_PERMISOS, getNivelRol } from '../utils/permisosHelper';
import { validarRUT, formatearRUT, formatearRUTMientrasEscribe } from '../utils/rutValidator';
import ConfirmModal from '../components/ConfirmModal';
import '../styles/MantenedorUsuarios.css';
import { LuUser, LuUserPlus, LuPencil, LuTrash2, LuX, LuSave, LuEye, LuEyeOff, LuShield } from 'react-icons/lu';
import GestionRolesModal from '../components/GestionRolesModal';

const MantenedorUsuarios = () => {
  const { usuario: usuarioActual } = useAuth();
  const { theme } = useTheme();
  const toast = useToast();

  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [catalogos, setCatalogos] = useState({ locales: [], grupos: [] });
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRolesOpen, setModalRolesOpen] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmEliminar, setConfirmEliminar] = useState(null);

  const [formData, setFormData] = useState({
    Rut: '',
    NombreUsu: '',
    Nombre: '',
    ApellidoPat: '',
    ApellidoMat: '',
    clave: '',
    rol_id: '',
    Local: '',
    GrupoId: ''
  });

  const [sugerencias, setSugerencias] = useState([]);
  const [nombreUsuDisponible, setNombreUsuDisponible] = useState(null);
  const [verificandoNombreUsu, setVerificandoNombreUsu] = useState(false);

  useEffect(() => {
    cargarUsuarios();
    cargarRoles();
    cargarCatalogos();
  }, []);

  const cargarUsuarios = async () => {
    setLoading(true);
    try {
      const response = await usuarioService.obtenerUsuarios();
      if (response.success) setUsuarios(response.data);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const cargarRoles = async () => {
    try {
      const response = await usuarioService.obtenerRoles();
      if (response.success) setRoles(response.data);
    } catch (error) {
      console.error('Error al cargar roles:', error);
    }
  };

  const cargarCatalogos = async () => {
    try {
      const response = await api.get('/catalogos');
      if (response.data.success) {
        let locales = response.data.data.locales;
        const grupos = response.data.data.grupos;
        if (getNivelRol(usuarioActual) < 7) {
          locales = locales.filter(local => local.CodLocal === usuarioActual.Local);
        }
        setCatalogos({ locales, grupos });
      }
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const handleAbrirModal = (usuario = null) => {
    if (usuario) {
      setModoEdicion(true);
      setUsuarioSeleccionado(usuario);
      setFormData({
        Rut: usuario.Rut || '',
        NombreUsu: usuario.NombreUsu,
        Nombre: usuario.Nombre,
        ApellidoPat: usuario.ApellidoPat,
        ApellidoMat: usuario.ApellidoMat || '',
        clave: '',
        rol_id: usuario.rol_id || '',
        Local: usuario.Local || '',
        GrupoId: usuario.GrupoId || ''
      });
      setNombreUsuDisponible(null);
    } else {
      setModoEdicion(false);
      setUsuarioSeleccionado(null);
      setFormData({
        Rut: '',
        NombreUsu: '',
        Nombre: '',
        ApellidoPat: '',
        ApellidoMat: '',
        clave: '',
        rol_id: '',
        Local: usuarioActual.Local || '',
        GrupoId: ''
      });
      setNombreUsuDisponible(null);
      setSugerencias([]);
    }
    setModalOpen(true);
  };

  const handleCerrarModal = () => {
    setModalOpen(false);
    setModoEdicion(false);
    setUsuarioSeleccionado(null);
    setFormData({ Rut: '', NombreUsu: '', Nombre: '', ApellidoPat: '', ApellidoMat: '', clave: '', rol_id: '', Local: '', GrupoId: '' });
    setSugerencias([]);
    setNombreUsuDisponible(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'Rut') {
      if (value.length <= 12) {
        setFormData(prev => ({ ...prev, [name]: formatearRUTMientrasEscribe(value) }));
      }
    } else if (name === 'NombreUsu') {
      const valorMayusculas = value.toUpperCase();
      if (valorMayusculas.length <= 3) {
        setFormData(prev => ({ ...prev, [name]: valorMayusculas }));
        if (valorMayusculas.length === 3) verificarNombreUsuario(valorMayusculas);
        else setNombreUsuDisponible(null);
      }
    } else if (name === 'clave') {
      if (value.length <= 4 && /^\d*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (name === 'Nombre' || name === 'ApellidoPat' || name === 'ApellidoMat') {
      const { Nombre, ApellidoPat, ApellidoMat } = { ...formData, [name]: value };
      if (Nombre && ApellidoPat) generarSugerencias(Nombre, ApellidoPat, ApellidoMat);
    }
  };

  const handleRutBlur = () => {
    if (formData.Rut) {
      const resultado = validarRUT(formData.Rut);
      if (!resultado.valido) {
        toast.error(resultado.mensaje);
      } else {
        setFormData(prev => ({ ...prev, Rut: formatearRUT(formData.Rut) }));
      }
    }
  };

  const verificarNombreUsuario = async (nombreUsu) => {
    if (nombreUsu.length !== 3) return;
    setVerificandoNombreUsu(true);
    try {
      const response = await usuarioService.verificarNombreUsuario(
        nombreUsu,
        modoEdicion ? usuarioSeleccionado.id : null
      );
      if (response.success) setNombreUsuDisponible(response.disponible);
    } catch (error) {
      console.error('Error al verificar nombre de usuario:', error);
    } finally {
      setVerificandoNombreUsu(false);
    }
  };

  const generarSugerencias = async (nombre, apellidoPat, apellidoMat) => {
    try {
      const response = await usuarioService.sugerirNombreUsuario(nombre, apellidoPat, apellidoMat);
      if (response.success) setSugerencias(response.sugerencias);
    } catch (error) {
      console.error('Error al generar sugerencias:', error);
    }
  };

  const handleSeleccionarSugerencia = (sugerencia) => {
    setFormData(prev => ({ ...prev, NombreUsu: sugerencia.valor }));
    setNombreUsuDisponible(sugerencia.disponible);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.Nombre || !formData.ApellidoPat || !formData.NombreUsu) {
      toast.warning('Complete los campos obligatorios');
      return;
    }
    if (formData.NombreUsu.length !== 3) {
      toast.warning('El nombre de usuario debe tener 3 caracteres');
      return;
    }
    if (!modoEdicion && formData.clave.length !== 4) {
      toast.warning('La contraseña debe tener 4 dígitos');
      return;
    }
    if (modoEdicion && formData.clave && formData.clave.length !== 4) {
      toast.warning('La contraseña debe tener 4 dígitos');
      return;
    }
    if (!formData.rol_id) {
      toast.warning('Debe seleccionar un rol');
      return;
    }
    if (formData.Rut && !validarRUT(formData.Rut).valido) {
      toast.error('RUT inválido');
      return;
    }

    setLoading(true);
    try {
      const datosEnviar = { ...formData };
      if (modoEdicion && !datosEnviar.clave) delete datosEnviar.clave;

      const response = modoEdicion
        ? await usuarioService.actualizarUsuario(usuarioSeleccionado.id, datosEnviar)
        : await usuarioService.crearUsuario(datosEnviar);

      if (response.success) {
        toast.success(response.message);
        handleCerrarModal();
        cargarUsuarios();
      }
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      toast.error(error.message || 'Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = (usuario) => {
    setConfirmEliminar(usuario);
  };

  const doEliminar = async () => {
    const usuario = confirmEliminar;
    setConfirmEliminar(null);
    setLoading(true);
    try {
      const response = await usuarioService.eliminarUsuario(usuario.id);
      if (response.success) {
        toast.success(response.message);
        cargarUsuarios();
      }
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      toast.error(error.message || 'Error al eliminar usuario');
    } finally {
      setLoading(false);
    }
  };

  const usuariosFiltrados = usuarios.filter(user =>
    user.Nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    user.ApellidoPat.toLowerCase().includes(busqueda.toLowerCase()) ||
    user.NombreUsu.toLowerCase().includes(busqueda.toLowerCase()) ||
    user.Rut?.includes(busqueda)
  );

  const puedeCrear = tienePermiso(usuarioActual, CODIGOS_PERMISOS.CREAR_USUARIOS);
  const puedeEditar = tienePermiso(usuarioActual, CODIGOS_PERMISOS.EDITAR_USUARIOS);
  const puedeEliminar = tienePermiso(usuarioActual, CODIGOS_PERMISOS.ELIMINAR_USUARIOS);
  const puedeGestionarRoles = tienePermiso(usuarioActual, CODIGOS_PERMISOS.VER_ROLES);
  const puedeSeleccionarLocal = getNivelRol(usuarioActual) >= 7;
  const puedeSeleccionarGrupo = getNivelRol(usuarioActual) >= 4;

  return (
    <div className="mantenedor-container">
      {/* Header */}
      <div className="mantenedor-header">
        <div>
          <h2>Mantenedor de Usuarios</h2>
          <p className="subtitle">Gestión de usuarios y roles del sistema</p>
        </div>
        <div className="header-actions">
          {puedeGestionarRoles && (
            <button className="btn-roles" onClick={() => setModalRolesOpen(true)}>
              <LuShield /> Gestionar Roles
            </button>
          )}
          {puedeCrear && (
            <button className="btn-primary" onClick={() => handleAbrirModal()}>
              <LuUserPlus /> Nuevo Usuario
            </button>
          )}
        </div>
      </div>

      {/* Buscador */}
      <div className="busqueda-card">
        <input
          type="text"
          placeholder="Buscar por nombre, apellido, usuario o RUT..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="busqueda-input"
        />
      </div>

      {/* Tabla de usuarios */}
      <div className="tabla-container">
        <div className="tabla-scroll-wrapper">
          <table className="usuarios-table">
            <thead>
              <tr>
                <th>RUT</th>
                <th>Usuario</th>
                <th>Nombre Completo</th>
                <th>Rol</th>
                <th>Iglesia</th>
                <th>Grupo</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="loading-cell">Cargando usuarios...</td>
                </tr>
              ) : usuariosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-cell">No se encontraron usuarios</td>
                </tr>
              ) : (
                usuariosFiltrados.map(user => (
                  <tr key={user.id}>
                    <td>{user.Rut || 'Sin RUT'}</td>
                    <td><strong>{user.NombreUsu}</strong></td>
                    <td>{user.Nombre} {user.ApellidoPat} {user.ApellidoMat}</td>
                    <td>
                      <span className="badge-rol" style={{
                        background: `hsl(${(user.rol?.nivel || 0) * 36}, 70%, ${theme === 'dark' ? '22%' : '90%'})`,
                        color: `hsl(${(user.rol?.nivel || 0) * 36}, 70%, ${theme === 'dark' ? '75%' : '30%'})`
                      }}>
                        {user.rol?.nombre || user.permisoInfo?.NombrePermiso || 'Sin rol'}
                      </span>
                    </td>
                    <td>{user.localInfo?.Nombre || 'Sin local'}</td>
                    <td>{user.grupoInfo?.Descripcion || user.grupoInfo?.Nombre || 'Sin grupo'}</td>
                    <td className="acciones-cell">
                      {puedeEditar && (
                        <button className="btn-icon btn-edit" onClick={() => handleAbrirModal(user)} title="Editar">
                          <LuPencil />
                        </button>
                      )}
                      {puedeEliminar && user.id !== usuarioActual.id && (
                        <button className="btn-icon btn-delete" onClick={() => handleEliminar(user)} title="Eliminar">
                          <LuTrash2 />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Usuario */}
      {modalOpen && (
        <div className="modal-overlay" onClick={handleCerrarModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <LuUser />
                {modoEdicion ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
              <button className="btn-close" onClick={handleCerrarModal}>
                <LuX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {/* Fila 1: RUT y Nombre Usuario */}
              <div className="form-row">
                <div className="form-group">
                  <label>RUT (opcional)</label>
                  <input
                    type="text"
                    name="Rut"
                    value={formData.Rut}
                    onChange={handleChange}
                    onBlur={handleRutBlur}
                    placeholder="12.345.678-9"
                    disabled={loading}
                  />
                  <small className="form-hint">Formato: 12.345.678-9</small>
                </div>

                <div className="form-group">
                  <label>Nombre de Usuario <span className="required">*</span></label>
                  <div className="input-with-indicator">
                    <input
                      type="text"
                      name="NombreUsu"
                      value={formData.NombreUsu}
                      onChange={handleChange}
                      placeholder="ABC"
                      maxLength="3"
                      required
                      disabled={loading}
                    />
                    {verificandoNombreUsu && <span className="indicator verificando">⏳</span>}
                    {nombreUsuDisponible === true && <span className="indicator disponible">✓</span>}
                    {nombreUsuDisponible === false && <span className="indicator no-disponible">✗</span>}
                  </div>
                  <small className="form-hint">3 caracteres (automático en mayúsculas)</small>

                  {sugerencias.length > 0 && !formData.NombreUsu && (
                    <div className="sugerencias">
                      <small>Sugerencias:</small>
                      <div className="sugerencias-list">
                        {sugerencias.map((sug, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`sugerencia-btn ${sug.disponible ? 'disponible' : 'no-disponible'}`}
                            onClick={() => handleSeleccionarSugerencia(sug)}
                            disabled={!sug.disponible}
                          >
                            {sug.valor} {sug.disponible ? '✓' : '✗'}
                            <span className="sugerencia-desc">{sug.descripcion}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Fila 2: Nombre y Apellido Paterno */}
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre <span className="required">*</span></label>
                  <input type="text" name="Nombre" value={formData.Nombre} onChange={handleChange} required disabled={loading} />
                </div>
                <div className="form-group">
                  <label>Apellido Paterno <span className="required">*</span></label>
                  <input type="text" name="ApellidoPat" value={formData.ApellidoPat} onChange={handleChange} required disabled={loading} />
                </div>
              </div>

              {/* Fila 3: Apellido Materno y Contraseña */}
              <div className="form-row">
                <div className="form-group">
                  <label>Apellido Materno</label>
                  <input type="text" name="ApellidoMat" value={formData.ApellidoMat} onChange={handleChange} disabled={loading} />
                </div>
                <div className="form-group">
                  <label>Contraseña {!modoEdicion && <span className="required">*</span>}</label>
                  <div className="input-with-icon">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="clave"
                      value={formData.clave}
                      onChange={handleChange}
                      placeholder="4 dígitos"
                      maxLength="4"
                      required={!modoEdicion}
                      disabled={loading}
                    />
                    <button type="button" className="btn-toggle-password" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <LuEyeOff /> : <LuEye />}
                    </button>
                  </div>
                  <small className="form-hint">{modoEdicion ? 'Dejar en blanco para no cambiar' : '4 dígitos numéricos'}</small>
                </div>
              </div>

              {/* Fila 4: Rol y Local */}
              <div className="form-row">
                <div className="form-group">
                  <label>Rol <span className="required">*</span></label>
                  <select name="rol_id" value={formData.rol_id} onChange={handleChange} required disabled={loading}>
                    <option value="">Seleccione un rol</option>
                    {roles.map(rol => (
                      <option key={rol.id} value={rol.id}>{rol.nombre} (Nivel {rol.nivel})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Iglesia {!puedeSeleccionarLocal && <span className="badge-bloqueado">🔒</span>}</label>
                  <select name="Local" value={formData.Local} onChange={handleChange} disabled={loading || !puedeSeleccionarLocal}>
                    <option value="">Seleccione una iglesia</option>
                    {catalogos.locales.map(local => (
                      <option key={local.id} value={local.CodLocal}>{local.Nombre}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Fila 5: Grupo */}
              <div className="form-group">
                <label>Grupo {!puedeSeleccionarGrupo && <span className="badge-bloqueado">🔒</span>}</label>
                <select name="GrupoId" value={formData.GrupoId} onChange={handleChange} disabled={loading || !puedeSeleccionarGrupo}>
                  <option value="">Seleccione un grupo</option>
                  {catalogos.grupos.map(grupo => (
                    <option key={grupo.id} value={grupo.id}>{grupo.Descripcion || grupo.Nombre}</option>
                  ))}
                </select>
              </div>

              {/* Botones */}
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={handleCerrarModal} disabled={loading}>
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || (formData.NombreUsu.length === 3 && nombreUsuDisponible === false)}
                >
                  <LuSave />
                  {loading ? 'Guardando...' : modoEdicion ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Gestión de Roles */}
      {modalRolesOpen && (
        <GestionRolesModal
          onClose={() => setModalRolesOpen(false)}
          onRolesActualizados={cargarRoles}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmEliminar}
        title="Eliminar Usuario"
        message={`¿Está seguro que desea eliminar al usuario ${confirmEliminar?.NombreUsu}?`}
        confirmLabel="Eliminar"
        onConfirm={doEliminar}
        onCancel={() => setConfirmEliminar(null)}
        danger
      />
    </div>
  );
};

export default MantenedorUsuarios;
